import React, { useEffect, useState } from "react";
import ContractMasthead from "./ContractMasthead";
import { Clause, Register, RegisterRow, Terms, Party } from "./ContractDocumentParts";
import { ContractSourceButton, ContractOptionRow, ContractSignatureClause, ContractVehicleSourceNote, ContractOdometerAttestation } from "./ContractReviewUi";
import { VerifiedTick } from "./StatusMark";
import { readContractUiUpdates } from "./contractUiRealtime";
import useContractChangeNotices from "./useContractChangeNotices";
import { confirmBuyerInspection } from "../../lib/contractDetails";
import { formatSekAmount } from "../../lib/format.jsx";

const formatSEK = (value) => (value ? formatSekAmount(value) : "-");

// Shared "review & confirm" body for the buyer journey (used by BuyerProfileFlow
// and BuyerSuccessFlow) — the purchase agreement as one document, matching the
// mock-up's ds-doc clause grammar. Presentation only: the parent owns the
// contract data and what "Confirm deal" does.
export default function BuyerReviewSections({ t, contractMainData, buyerName, loading, onConfirm, messages = [], signedAt = {}, signing = false, onCancelSigning, sellerProfile = null, buyerProfile = null }) {
  // The buyer's confirmation is kept on the contract: restored when they come back, saved when given.
  // inspectedAt is undefined until this contract's saved value has been read.
  const [buyerInspected, setBuyerInspected] = useState(Boolean(contractMainData?.buyerInspectionConfirmedAt));
  const [inspectedAt, setInspectedAt] = useState(undefined);
  useEffect(() => {
    setBuyerInspected(Boolean(contractMainData?.buyerInspectionConfirmedAt));
    setInspectedAt(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractMainData?.id]);
  // Tell the buyer what the seller changes while they are looking at the page.
  useContractChangeNotices(messages, t);
  const hasFourWheelDrive = contractMainData?.carInfoFourWheelDrive != null && contractMainData.carInfoFourWheelDrive !== "";
  const fourWheelDrive = String(contractMainData?.carInfoFourWheelDrive).toLowerCase() === "true";
  const liveFields = readContractUiUpdates(messages);
  const multiline = (value) => String(value || "").split("\n").map((line) => line.trim()).filter(Boolean);
  // First-load default only: once the seller has actually set a value (live
  // or persisted), that value takes over exactly as before — this only
  // covers the gap before either has ever fired.
  const debtOption = liveFields.debt?.option ?? contractMainData?.debtOption ?? "none";
  const loanOption = liveFields.loan?.option ?? contractMainData?.loanLienOption ?? "none";
  // the seller's live edit, else what is saved; unedited equipment shows the standard text the seller sees
  const equipmentText = liveFields.equipment ?? contractMainData?.equipmentIncluded ?? t("agreement.equipmentStandardText");
  const knownDefectsText = liveFields.knownDefects ?? contractMainData?.knownDefects;
  const withLiveDetails = (label, details, includeBank = false) => {
    const suffix = [includeBank && details?.bankName, details?.amount > 0 && formatSEK(details.amount)].filter(Boolean).join(" · ");
    return suffix ? `${label} · ${suffix}` : label;
  };

  // Every column on tbl_CarInfoMain, styled per the mock-up's register-row
  // layout, plus the mock-up-only concepts we have no column for (vin, color,
  // engine, number of keys) — shown anyway, always empty, since the mock-up
  // always shows them. Nothing hidden, nothing renamed away.
  const vehicleFacts = contractMainData
    ? [
        ["vin", null],
        ["color", null],
        ["registrationNo", contractMainData.carInfoRegistrationNo],
        ["vehicleDesignation", contractMainData.carInfoVehicleDesignation],
        ["tradeName", contractMainData.carInfoTradeName],
        // "Year model" is the primary model-year field; Bilvision's Årsmodell
        // (YearModel) is often blank, so fall back to Fordonsår (VehicleYears)
        // rather than showing both/neither.
        ["yearModel", contractMainData.carInfoYearModel || contractMainData.carInfoVehicleYears],
        ["engine", null],
        ["fuel", contractMainData.carInfoFuel],
        ["transmission", contractMainData.carInfoWaxelbarge],
        ["drivetrain", hasFourWheelDrive ? t(fourWheelDrive ? "buyer.fourWheelDriveYes" : "buyer.fourWheelDriveNo") : null],
        ["electricVehicleConfiguration", contractMainData.carInfoElectricVehicleConfiguration],
        ["firstInTraffic", contractMainData.carInfoRegistrationDate],
        ["previousOwners", contractMainData.carInfoNumberOfUsers],
        ["inspectionLast", contractMainData.carInfoInspectionDate],
        ["inspectionNext", contractMainData.carInfoInspectionDateEMPTY],
        ["previousInspectionDate", contractMainData.carInfoPreviousInspectionDate],
        ["dispensationInspectionGroup", contractMainData.carInfoDispensationInspectionGroup],
        ["inspectionGroup", contractMainData.carInfoInspectionGroup],
        ["inspectionStation", contractMainData.carInfoInspectionStation],
        ["inspectionProgramCode", contractMainData.carInfoInspectionProgramCode],
        ["previousInspectionProgramCode", contractMainData.carInfoPreviousInspectionProgramCode],
        // live change from the seller first, then what is saved on the contract, then the registry value
        ["odometer", liveFields.odometer ?? contractMainData.odometerKm ?? contractMainData.carInfoFeedingStall],
        ["keysCount", liveFields.keysCount ?? contractMainData.keysCount ?? null],
        ["tax", contractMainData.carInfoTax],
        ["malus", contractMainData.carInfoMalus],
      ]
    : [];

  return (
    <div className="ds-form ds-contract-review ds-contract-review--seller mx-auto w-full">
      {!contractMainData ? (
        <div className="overflow-hidden rounded-[13px] border border-ds-rule-edge bg-ds-sheet p-2">
          {Array.from({ length: 12 }).map((_, idx) => (
            <div key={idx} className="mx-3 my-2 h-4 animate-pulse rounded bg-[rgba(20,36,46,.08)]" style={{ width: "60%" }} />
          ))}
        </div>
      ) : (
        <article className="ds-doc ds-doc--terms">
          <ContractMasthead
            t={t}
            regNo={contractMainData.carInfoRegistrationNo}
            vehicleDesignation={contractMainData.carInfoVehicleDesignation}
            vehicleYears={contractMainData.carInfoVehicleYears}
            vehicleType={contractMainData.vahicleTypeOptionName}
            sellerName={contractMainData.sellerUserFullName}
            buyerName={buyerName}
            lastChangedAt={contractMainData.lastModificationTime || contractMainData.creationTime}
          />

          <Clause
            id="section-vehicle"
            number={1}
            title={t("buyer.vehicleInformation")}
            aside={<ContractSourceButton t={t} contract={contractMainData} referenceLayout />}
          >
            <Register>
              {vehicleFacts.map(([key, val]) => (
                <RegisterRow
                  key={key}
                  label={t(`buyer.${key}`)}
                  note={key === "odometer" && (
                    contractMainData.sellerDealComplete ? (
                      <span className="ds-doc-attest-line">
                        <VerifiedTick width={12} height={10} />
                        <span>{t("contractUi.certifiedBy").replace("{name}", contractMainData.sellerUserFullName || t("role.seller"))}</span>
                      </span>
                    ) : t("contractUi.sellerOdometerNotCertified")
                  )}
                >
                  {val || <span className="ds-doc-value--absent">{t("agreement.notSet")}</span>}
                </RegisterRow>
              ))}
              <RegisterRow label={t("seller.car.steps.valuation")}>
                {contractMainData.carValuationBySeller
                  ? contractMainData.carValuationBySeller.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                  : "-"}
              </RegisterRow>
              <RegisterRow label={t("buyer.equipmentIncluded")} wide>
                {multiline(equipmentText).length > 0 ? (
                  <span className="ds-doc-value">
                    {multiline(equipmentText).map((line, index) => <span key={`${line}:${index}`} className="block">{line}</span>)}
                  </span>
                ) : <span className="ds-doc-value--absent">{t("buyer.equipmentNone")}</span>}
              </RegisterRow>
            </Register>
            <ContractVehicleSourceNote t={t} />
          </Clause>

          <Clause id="section-parties" number={2} title={t("agreement.partiesTitle")}>
            <div className="ds-doc-parties">
              <Party t={t} roleKey="seller" name={contractMainData.sellerUserFullName} profile={sellerProfile} />
              <Party t={t} roleKey="buyer" name={buyerName} profile={buyerProfile} />
            </div>
          </Clause>

          <Clause id="section-price" number={3} title={t("contractUi.purchasePriceTitle")}>
            <div className="ds-doc-price-line">
              <p className="ds-doc-price">{formatSEK(contractMainData.carValuationBySeller)}</p>
            </div>
          </Clause>

          <Clause id="section-delivery" number={4} title={t("contractUi.deliveryTitle")}>
            <Terms clause={4} items={[t("contractUi.deliveryRiskText")]} />
          </Clause>

          <Clause id="section-condition" number={5} title={t("seller.conditionDefects.title")}>
            <Terms clause={5} items={[t("contractUi.conditionAsIs"), t("contractUi.conditionClaim"), t("contractUi.conditionNotify")]} />
            <Register className="mt-4">
              <RegisterRow label={t("buyer.knownDefects")} wide>
                {multiline(knownDefectsText).length > 0 ? (
                  <span className="ds-doc-value">
                    {multiline(knownDefectsText).map((line, index) => <span key={`${line}:${index}`} className="block">{line}</span>)}
                  </span>
                ) : <span className="ds-doc-value--absent">{t("agreement.notSet")}</span>}
              </RegisterRow>
            </Register>
            <ContractOdometerAttestation
              key={`${contractMainData.id}:${buyerName}`}
              id="buyer-inspected-checkbox"
              actionId="buyer-inspected"
              statement={t("contractUi.buyerInspectionStatement")}
              actionLabel={t("contractUi.buyerConfirm")}
              recordLabel={t("contractUi.buyerConfirmed")}
              pendingText={t("contractUi.buyerNotConfirmed")}
              showDate={false}
              t={t}
              canAct
              initialCertifiedAt={inspectedAt === undefined ? contractMainData.buyerInspectionConfirmedAt : inspectedAt}
              onChange={(confirmed, at) => {
                setBuyerInspected(confirmed);
                setInspectedAt(confirmed ? at : null);
                void confirmBuyerInspection(contractMainData.id, confirmed);
              }}
            />
          </Clause>

          <Clause id="section-debt" number={6} title={t("contractUi.debtsLoansTitle")}>
            <Register>
              <ContractOptionRow t={t} label={t("buyer.debtsTitle")} value={debtOption}
                options={["none", "seller", "buyer"].map((option) => ({ value: option, label: withLiveDetails(t(`buyer.debts${option[0].toUpperCase()}${option.slice(1)}`), option === debtOption ? liveFields.debt : null) }))} />
              <ContractOptionRow t={t} label={t("contractUi.loanTitle")} value={loanOption}
                options={["none", "seller", "buyer"].map((option) => ({ value: option, label: withLiveDetails(t(`buyer.loan${option[0].toUpperCase()}${option.slice(1)}`), option === loanOption ? liveFields.loan : null, true) }))} />
            </Register>
            <p className="ds-doc-source">{t("contractUi.debtsLoansSource")}</p>
          </Clause>

          <Clause id="section-ownership" number={7} title={t("contractUi.ownershipTitle")}>
            <Terms clause={7} items={[t("contractUi.ownershipRetention"), t("contractUi.ownershipDocuments"), t("contractUi.ownershipDrivingBan")]} />
          </Clause>

          <Clause id="section-transport" number={8} title={t("contractUi.transportTitle")}>
            <Terms clause={8} items={[t("contractUi.transportCommitment"), t("contractUi.transportNote")]} />
          </Clause>

          <ContractSignatureClause
            t={t}
            role="buyer"
            contract={contractMainData}
            sellerName={contractMainData.sellerUserFullName}
            buyerName={buyerName}
            loading={loading}
            onConfirm={onConfirm}
            canConfirm={buyerInspected}
            remaining={[
              ...(!buyerInspected ? [{ label: t("contractUi.remainingInspection"), targetId: "buyer-inspected-checkbox" }] : []),
            ]}
            signedAt={signedAt}
            signing={signing}
            onCancelSigning={onCancelSigning}
          />
        </article>
      )}
    </div>
  );
}
